
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Database, ArrowLeft, Save, RefreshCw } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { toast } from "@/components/ui/sonner";

const DatabaseSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    autoBackup: true,
    backupInterval: '24',
    retentionDays: 30,
    compressionEnabled: true,
    maintenanceWindow: '02:00',
    queryTimeout: 30,
    maxConnections: 100
  });

  const handleSave = () => {
    toast.success("Database settings saved successfully");
  };

  const handleTestConnection = () => {
    toast.success("Database connection test successful");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')}>
          <ArrowLeft className="h-4 w-4" />
          Back to System Settings
        </Button>
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold">Database Configuration</h1>
        </div>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Connection Settings</CardTitle>
            <CardDescription>Configure database connection parameters</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="maxConnections">Max Connections</Label>
                <Input
                  id="maxConnections"
                  type="number"
                  value={settings.maxConnections}
                  onChange={(e) => setSettings({...settings, maxConnections: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <Label htmlFor="queryTimeout">Query Timeout (seconds)</Label>
                <Input
                  id="queryTimeout"
                  type="number"
                  value={settings.queryTimeout}
                  onChange={(e) => setSettings({...settings, queryTimeout: parseInt(e.target.value)})}
                />
              </div>
            </div>
            <Button variant="outline" onClick={handleTestConnection} className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Test Connection
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Backup Configuration</CardTitle>
            <CardDescription>Automated backup settings and data retention</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Enable Automatic Backup</Label>
                <p className="text-sm text-muted-foreground">Automatically backup database at scheduled intervals</p>
              </div>
              <Switch
                checked={settings.autoBackup}
                onCheckedChange={(checked) => setSettings({...settings, autoBackup: checked})}
              />
            </div>
            
            {settings.autoBackup && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="backupInterval">Backup Interval (hours)</Label>
                  <Select value={settings.backupInterval} onValueChange={(value) => setSettings({...settings, backupInterval: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">Every 6 hours</SelectItem>
                      <SelectItem value="12">Every 12 hours</SelectItem>
                      <SelectItem value="24">Daily</SelectItem>
                      <SelectItem value="168">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="retentionDays">Retention Period (days)</Label>
                  <Input
                    id="retentionDays"
                    type="number"
                    value={settings.retentionDays}
                    onChange={(e) => setSettings({...settings, retentionDays: parseInt(e.target.value)})}
                  />
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <Label>Enable Compression</Label>
              <Switch
                checked={settings.compressionEnabled}
                onCheckedChange={(checked) => setSettings({...settings, compressionEnabled: checked})}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Maintenance</CardTitle>
            <CardDescription>Database maintenance and optimization settings</CardDescription>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="maintenanceWindow">Maintenance Window</Label>
              <Input
                id="maintenanceWindow"
                type="time"
                value={settings.maintenanceWindow}
                onChange={(e) => setSettings({...settings, maintenanceWindow: e.target.value})}
              />
              <p className="text-sm text-muted-foreground mt-1">Time for automated maintenance tasks</p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DatabaseSettings;
